/**
 * FracSight 甜点评价 — 数据置信度、工程风险与安全评分。
 *
 * 职责：
 * 1. 按统一评分方向（positive / negative / interval / risk）安全评分，
 *    必须防御空数组、max=min、NaN、Infinity、极端值、非法单位与缺失数据。
 * 2. 渗透率等跨数量级参数支持 log10 变换（不得对 0 或负值取对数）。
 * 3. 计算 Data Confidence（0～1）与 Engineering Risk（0～1），并给出可解释原因。
 * 4. 结果仅为工程辅助判断与风险提示，不得包装成确定性预测。
 */
import type {
  IntervalScoringConfig, ParameterValueMap, ScoreDirection,
  SweetSpotParameterDefinition,
} from './types';

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** 安全 log10 变换：仅对正数生效，0/负数/非法值返回 null。 */
export function safeLog10(value: number | null | undefined): number | null {
  if (!isFiniteNumber(value) || value <= 0) return null;
  return Math.log10(value);
}

/**
 * 区间评分（梯形）：不可接受区 0 → 过渡区线性 → 最优区 1 → 过渡区 → 不可接受区 0。
 * 未配置区间边界时返回 null（不参与计算），避免无依据编造全国通用阈值。
 */
export function scoreInterval(
  value: number | null | undefined,
  config: IntervalScoringConfig | undefined,
): number | null {
  if (!isFiniteNumber(value)) return null;
  if (!config) return null;
  const { minAcceptable, optimalMin, optimalMax, maxAcceptable } = config;
  const hasLower = isFiniteNumber(minAcceptable) && isFiniteNumber(optimalMin);
  const hasUpper = isFiniteNumber(optimalMax) && isFiniteNumber(maxAcceptable);
  if (!hasLower && !hasUpper) return null;
  // 最优区命中
  if (isFiniteNumber(optimalMin) && isFiniteNumber(optimalMax) && value >= optimalMin && value <= optimalMax) return 1;
  // 低于最优区
  if (hasLower && value < optimalMin!) {
    if (value <= minAcceptable!) return 0;
    if (optimalMin! <= minAcceptable!) return 0;
    return clamp01((value - minAcceptable!) / (optimalMin! - minAcceptable!));
  }
  // 高于最优区
  if (hasUpper && value > optimalMax!) {
    if (value >= maxAcceptable!) return 0;
    if (maxAcceptable! <= optimalMax!) return 0;
    return clamp01((maxAcceptable! - value) / (maxAcceptable! - optimalMax!));
  }
  // 只有单侧边界
  if (isFiniteNumber(value) && hasLower && !hasUpper && value < optimalMin!) {
    if (value <= minAcceptable!) return 0;
    return clamp01((value - minAcceptable!) / (optimalMin! - minAcceptable!));
  }
  return 1;
}

/**
 * 按评分方向安全评分（0～1）。
 * - positive: (value - min) / (max - min)，max=min 时取 0.5。
 * - negative: (max - value) / (max - min)，max=min 时取 0.5。
 * - interval: 梯形区间评分。
 * - risk: 数值越高风险越高，返回原始风险度（用于工程风险，不进入正向得分）。
 * 极端值（NaN/Infinity）返回 null，不参与计算。
 */
export function scoreByDirection(
  value: number | null | undefined,
  direction: ScoreDirection,
  range: { min: number; max: number },
  interval?: IntervalScoringConfig,
  useLog10 = false,
): number | null {
  let raw = value;
  if (useLog10) raw = safeLog10(value);
  if (!isFiniteNumber(raw)) return null;
  if (direction === 'interval') return scoreInterval(raw, interval);
  let min = range.min;
  let max = range.max;
  if (useLog10) {
    const logMin = safeLog10(range.min);
    const logMax = safeLog10(range.max);
    if (logMin == null || logMax == null) return null;
    min = logMin;
    max = logMax;
  }
  if (!isFiniteNumber(min) || !isFiniteNumber(max)) return null;
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  if (hi === lo) return 0.5;
  if (direction === 'negative') return clamp01((hi - raw) / (hi - lo));
  // positive 与 risk 均按越大越强的单调映射；risk 的语义由调用方单独解释。
  return clamp01((raw - lo) / (hi - lo));
}

export type RiskItem = {
  code: string;
  name: string;
  score: number;
  level: 'low' | 'medium' | 'high';
  message: string;
};

export type EngineeringRiskResult = {
  score: number;
  level: 'low' | 'medium' | 'high';
  items: RiskItem[];
};

export const RISK_LEVEL_THRESHOLD = { high: 0.66, medium: 0.33 };

export function riskLevel(score: number): 'low' | 'medium' | 'high' {
  if (score >= RISK_LEVEL_THRESHOLD.high) return 'high';
  if (score >= RISK_LEVEL_THRESHOLD.medium) return 'medium';
  return 'low';
}

/**
 * 工程风险计算：独立于 CQ 正向得分。
 * 输入为风险参数定义与当前点位数值；无风险数据时返回 0 分与空风险项。
 * 输出仅作为风险提示与工程辅助判断。
 */
export function computeEngineeringRisk(
  values: ParameterValueMap,
  definitions: SweetSpotParameterDefinition[],
  ranges: Partial<Record<string, { min: number; max: number }>>,
): EngineeringRiskResult {
  const items: RiskItem[] = [];
  definitions.filter((definition) => definition.direction === 'risk').forEach((definition) => {
    const raw = values[definition.code.toLowerCase() as keyof ParameterValueMap];
    if (!isFiniteNumber(raw)) return;
    const range = ranges[definition.code.toLowerCase()];
    if (!range) return;
    const score = scoreByDirection(raw, 'risk', range, undefined, false);
    if (score == null) return;
    const level = riskLevel(score);
    items.push({
      code: definition.code,
      name: definition.name,
      score,
      level,
      message: `${definition.name}风险${level === 'high' ? '较高' : level === 'medium' ? '中等' : '较低'}（风险度 ${score.toFixed(2)}）。`,
    });
  });
  if (!items.length) return { score: 0, level: 'low', items };
  const score = clamp01(items.reduce((sum, item) => sum + item.score, 0) / items.length);
  return { score, level: riskLevel(score), items };
}

export type ConfidenceInput = {
  requiredCompleteness: number;
  recommendedCompleteness: number;
  matchRatio: number;
  unitValidRatio: number;
  abnormalRatio: number;
  alternativeRatio: number;
  manualRatio: number;
  hasBlockThresholds: boolean;
};

export type ConfidenceResult = {
  score: number;
  level: 'high' | 'medium' | 'low';
  reasons: string[];
};

/** 数据置信度（0～1）：综合考虑必选/推荐完整度、匹配、单位、异常、替代、人工与区块阈值。 */
export function computeDataConfidence(input: ConfidenceInput): ConfidenceResult {
  const reasons: string[] = [];
  if (input.requiredCompleteness < 1) reasons.push(`必选参数完整度 ${(input.requiredCompleteness * 100).toFixed(0)}%，存在缺失`);
  if (input.recommendedCompleteness < 1) reasons.push(`推荐参数完整度 ${(input.recommendedCompleteness * 100).toFixed(0)}%，存在缺失`);
  if (input.matchRatio < 1) reasons.push(`DataHub 匹配率 ${(input.matchRatio * 100).toFixed(0)}%，存在候选或未匹配`);
  if (input.unitValidRatio < 1) reasons.push('存在单位异常或单位不明的参数');
  if (input.abnormalRatio > 0) reasons.push(`数据异常比例 ${(input.abnormalRatio * 100).toFixed(0)}%`);
  if (input.alternativeRatio > 0) reasons.push(`替代参数比例 ${(input.alternativeRatio * 100).toFixed(0)}%`);
  if (input.manualRatio > 0) reasons.push(`人工录入比例 ${(input.manualRatio * 100).toFixed(0)}%`);
  if (!input.hasBlockThresholds) reasons.push('未加载区块标定阈值，采用相对评价');

  const weights = {
    required: 0.34,
    recommended: 0.18,
    match: 0.18,
    unit: 0.10,
    abnormal: 0.08,
    alternative: 0.04,
    manual: 0.04,
    thresholds: 0.04,
  };
  let score = weights.required * input.requiredCompleteness
    + weights.recommended * input.recommendedCompleteness
    + weights.match * input.matchRatio
    + weights.unit * input.unitValidRatio
    + weights.abnormal * (1 - input.abnormalRatio)
    + weights.alternative * (1 - input.alternativeRatio)
    + weights.manual * (1 - input.manualRatio)
    + weights.thresholds * (input.hasBlockThresholds ? 1 : 0.5);
  score = clamp01(score);
  const level = score >= 0.8 ? 'high' : score >= 0.6 ? 'medium' : 'low';
  if (level === 'low' && !reasons.length) reasons.push('基础数据质量较低');
  return { score, level, reasons };
}

export function confidenceLevelLabel(level: 'high' | 'medium' | 'low'): string {
  if (level === 'high') return '高';
  if (level === 'medium') return '中';
  return '低';
}

export function riskLevelLabel(level: 'low' | 'medium' | 'high'): string {
  if (level === 'high') return '高';
  if (level === 'medium') return '中';
  return '低';
}
